/**
 * Hook to orchestrate app initialization, API service setup, and Service Worker updates
 *
 * Manages:
 * - Service Worker registration and automatic version detection
 * - API Service initialization (getUserDetail call to establish bailleurId)
 * - App readiness state (ready to render router)
 * - Update state (SW update in progress)
 * - Single loading state combining all phases
 *
 * API Service Initialization:
 * - Calls getUserDetail() once at startup
 * - Response interceptor populates bailleurId (establishes authentication state)
 * - Promise resolves regardless of auth status (401 or success)
 *
 * SW Update Detection:
 * - Startup: Uses needRefresh state from vite-plugin-pwa (Workbox waiting event)
 * - Runtime: Uses swState (X-Service-Worker-Version header from API responses)
 * - Both paths trigger onSwVersionChanged() to activate new SW without user confirmation
 *
 * Returns isLoading: true during init, API setup, or SW update (show AppInitializer screen)
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { useSWState } from '../contexts/SWStateContext';
import { useApiServiceContext } from '../contexts/ApiServiceContext';
import { useSWVersionCheck } from './useSWVersionCheck';
import { trackEvent } from '../services/AppInsightsPlugin';
import { isSWAvailable, isProduction } from '../config';
import { Logger } from '../utils/Logger';

interface UseAppInitializationReturn {
  isLoading: boolean;
}

/**
 * Orchestrate app initialization and Service Worker updates
 *
 * Returns isLoading state: true during initialization or SW update
 */
export function useAppInitialization(): UseAppInitializationReturn {
  Logger.debug('APP', "useAppInitialization hook starting");

  const [ready, setReady] = useState(isSWAvailable ? false : true);
  const [apiReady, setApiReady] = useState(false);
  const [isSWUpdateInProgress, setShowUpdateOverlay] = useState(false);
  // If SW disabled, skip activation check. If SW enabled, check if already controlling the page
  // (avoid 5s timeout on normal reloads - only wait for SW_ACTIVATED_ON_LOAD on first install)
  const [swActivated, setSwActivated] = useState(
    !isSWAvailable || !!navigator.serviceWorker?.controller
  );

  const versionChangeHandledRef = useRef<boolean>(false);
  const apiInitializedRef = useRef<boolean>(false);
  const registrationRef = useRef<ServiceWorkerRegistration | null>(null);

  const { state } = useSWState();
  const apiService = useApiServiceContext();

  // Register Service Worker ONLY if enabled
  // When isSWAvailable=false, skip useRegisterSW entirely to prevent background registration
  const swRegistration = isSWAvailable ? useRegisterSW({
    onRegisteredSW(swUrl, r) {
      Logger.debug('SW', `✅ Service Worker registered successfully`);
      Logger.debug('SW', `URL: ${swUrl}`);
      Logger.debug('SW', `Environment: ${isProduction ? 'PRODUCTION' : 'DEVELOPMENT'}`);
      Logger.debug('SW', `Registration object available: ${!!r}`);
      registrationRef.current = r ?? null;

      // Track SW registration in Application Insights
      trackEvent('SW_REGISTERED', {
        environment: isProduction ? 'PRODUCTION' : 'DEVELOPMENT',
        swUrl: swUrl
      });

      // Workbox gère la détection automatique des mises à jour via needRefresh
      // Si une nouvelle version du SW est détectée, needRefresh deviendra true
      Logger.debug('SW', `✅ Service Worker registered, Workbox will detect updates automatically`);
      setReady(true);
    },
    onRegisterError(error) {
      Logger.error('SW', '❌ Service Worker registration failed:', error);
      Logger.error('SW', 'Falling back to offline-free mode (Service Worker unavailable)');
      setReady(true);
    },
    onOfflineReady() {
      Logger.debug('SW', "Offline mode ready");
    }
  }) : { needRefresh: [false], updateServiceWorker: () => {} };

  const { needRefresh: [needRefresh], updateServiceWorker } = swRegistration;

  // Memoize onUpdateStarted callback to prevent infinite re-renders
  // Without memoization, the callback changes every render, causing onSwVersionChanged to change,
  // which triggers useEffect dependencies and re-renders infinitely
  const onUpdateStarted = useCallback(() => {
    setShowUpdateOverlay(true);
  }, []);

  // Service Worker version check hook
  const { onSwVersionChanged } = useSWVersionCheck(
    updateServiceWorker,
    onUpdateStarted
  );

  // Listen for SW activation confirmation (SW_ACTIVATED_ON_LOAD)
  // This ensures the SW has called clients.claim() before getUserDetail() is called
  useEffect(() => {
    if (!isSWAvailable || swActivated) {
      return; // Already activated or SW disabled
    }

    const handleSWActivation = (event: MessageEvent) => {
      if (event.data?.type === 'SW_ACTIVATED_ON_LOAD') {
        Logger.debug('APP', '✅ SW_ACTIVATED_ON_LOAD received - SW is now controlling requests');
        setSwActivated(true);
      }
    };

    navigator.serviceWorker?.addEventListener('message', handleSWActivation);

    // Timeout fallback: If no activation message after 5s, proceed anyway
    const timeout = setTimeout(() => {
      Logger.debug('APP', '⚠️ SW activation timeout (5s) - proceeding anyway');
      setSwActivated(true);
    }, 5000);

    return () => {
      navigator.serviceWorker?.removeEventListener('message', handleSWActivation);
      clearTimeout(timeout);
    };
  }, [isSWAvailable, swActivated]);

  // Détection au démarrage: needRefresh devient true quand nouveau SW en attente
  useEffect(() => {
    if (needRefresh && !versionChangeHandledRef.current) {
      Logger.debug('APP', '🔄 needRefresh detected: New Service Worker waiting, triggering update');
      versionChangeHandledRef.current = true;
      onSwVersionChanged();
    }
  }, [needRefresh, onSwVersionChanged]);

  // Détection en runtime: state émet SW_VERSION_CHANGED via header X-Service-Worker-Version
  // Complémentaire à needRefresh qui gère le démarrage via Workbox waiting event
  useEffect(() => {
    if (!isSWAvailable) return; // Ignoré si SW désactivé : l'ancien SW peut encore broadcaster
    if (state.phase === 'SW_UPDATING' && !versionChangeHandledRef.current) {
      Logger.debug('APP', '🔄 Service Worker version changed, triggering update');
      versionChangeHandledRef.current = true;
      onSwVersionChanged();
    }
  }, [state.phase, onSwVersionChanged]);

  // Initialize API Service by calling getUserDetail() once
  // This populates bailleurId via the response interceptor and establishes authentication state
  // Only call after:
  // 1. SW is registered (ready=true)
  // 2. SW is activated and controlling requests (swActivated=true)
  // 3. No new version is waiting (needRefresh=false)
  useEffect(() => {
    if (apiInitializedRef.current) {
      return; // Already initialized
    }

    // Wait until SW is ready AND activated AND latest version is active (no pending update)
    if (!ready || !swActivated || needRefresh) {
      return; // Not ready yet, wait for next effect execution
    }

    const controller = new AbortController();
    apiInitializedRef.current = true;

    Logger.debug('APP', 'Initializing API service - calling getUserDetail()');

    apiService.getUserDetail(controller.signal)
      .then(() => {
        Logger.debug('APP', '✅ API service initialized - bailleurId populated via interceptor');
        setApiReady(true);
      })
      .catch((error) => {
        // 401 or other error: not authenticated, but API is ready to use
        Logger.debug('APP', 'API service initialization completed (not authenticated)', error);
        setApiReady(true);
      });

    return () => {
      controller.abort();
    };
  }, [apiService, ready, swActivated, needRefresh]);

  // Check for new SW version each time the app comes back to the foreground.
  // registration.update() makes a conditional HTTP request (fast, ~304 if no change).
  // Offline: fails silently (immediate network error, no visible effect).
  // If a new SW is found: needRefresh becomes true → useSWVersionCheck triggers overlay + reload.
  useEffect(() => {
    if (!isSWAvailable) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        Logger.debug('APP', '🔄 Page visible - checking for SW update...');
        registrationRef.current?.update().catch(() => {
          // Offline or network error: ignore silently
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Déclencher une sync SW quand le réseau est restauré (mode avion → connecté)
  useEffect(() => {
    if (!isSWAvailable) return;

    const handleOnline = () => {
      Logger.debug('APP', '🌐 Network restored - triggering SW sync...');
      navigator.serviceWorker?.controller?.postMessage({ type: 'TRIGGER_SYNC' });
    };

    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, []);

  return {
    isLoading: !ready || !apiReady || isSWUpdateInProgress
  };
}
